import React, { useState } from "react";
import { X } from "lucide-react";
import api from "../api";
import { Bt, Sl, Inp } from "./index";

// All Indian states + union territories, for the GSTIN "State" picker.
const INDIA_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

// Real PAN / GSTIN formats — India's PAN is always 5 letters, 4 digits,
// 1 letter (10 chars); GSTIN is a 15-char code (2-digit state code + PAN
// + entity/checksum characters). Validated on save so a mistyped or
// truncated PAN/GSTIN can't be saved silently.
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

// Add / Edit a vendor.
// Used from Settings → Vendors and inline from the Add Vendor Bill modal
// (the "+ New" button next to the vendor picker).
export const VendorModal = ({ cur, companies = [], onClose, onSaved }) => {
  const isEdit = !!cur?.id;
  const init = isEdit ? {
    name: cur.name || '', pan: cur.pan || '', gstin: cur.gstin || '', state: cur.state || '',
    bank_name: cur.bankName || '', account_number: cur.account || '', ifsc_code: cur.ifsc || '', payment_mode: cur.mode || 'NEFT',
    address: cur.address || '', contact_person: cur.contactName || '', contact_phone: cur.contactPhone || '', contact_email: cur.contactEmail || '',
    default_category: cur.defaultCategory || '', default_tds_section: cur.defaultTdsSection || '', default_tds_rate: String(cur.defaultTdsRate || '0'),
    company_id: cur.companyId ? String(cur.companyId) : '', notes: cur.notes || '',
  } : {
    name: '', pan: '', gstin: '', state: '',
    bank_name: '', account_number: '', ifsc_code: '', payment_mode: 'NEFT',
    address: '', contact_person: '', contact_phone: '', contact_email: '',
    default_category: '', default_tds_section: '', default_tds_rate: '0',
    company_id: '', notes: '',
  };
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const save = async () => {
    if (!f.name.trim()) return setErr('Vendor name is required');
    if (!f.pan.trim()) return setErr('PAN is required');
    if (!PAN_RE.test(f.pan.trim())) return setErr('PAN must be exactly 10 characters in the format AAAAA9999A');
    if (f.gstin.trim() && !GSTIN_RE.test(f.gstin.trim())) return setErr('GSTIN must be exactly 15 characters in a valid GSTIN format');
    if (!f.contact_person.trim()) return setErr('Contact Person is required');
    if (!f.contact_phone.trim()) return setErr('Phone is required');
    setSaving(true); setErr('');
    const body = {
      name: f.name.trim(), pan: f.pan || null, gstin: f.gstin || null, state: f.state || null,
      bank_name: f.bank_name || null, account_number: f.account_number || null, ifsc_code: f.ifsc_code || null,
      payment_mode: f.payment_mode || 'NEFT',
      address: f.address || null, contact_person: f.contact_person || null, contact_phone: f.contact_phone || null, contact_email: f.contact_email || null,
      default_category: f.default_category || null, default_tds_section: f.default_tds_section || null,
      default_tds_rate: parseFloat(f.default_tds_rate) || 0,
      company_id: f.company_id ? parseInt(f.company_id) : null, notes: f.notes || null,
    };
    try {
      const res = isEdit
        ? await api.put('/api/vendors/' + cur.id, body)
        : await api.post('/api/vendors', body);
      onSaved(isEdit ? cur.id : res?.id);
    } catch (e) { setErr(e.message || 'Save failed'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[936px] max-h-[90vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-4"><span className="text-[18px] font-extrabold text-t0">{isEdit ? 'Edit Vendor' : 'Add Vendor'}</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Basic</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-4">
          <div><div className="text-[14px] text-t2 mb-[3px]">Name <span className="text-r">*</span></div><Inp value={f.name} onChange={v => setF(p => ({ ...p, name: v }))} placeholder="e.g. Acme Realty" /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">PAN <span className="text-r">*</span></div><Inp value={f.pan} onChange={v => setF(p => ({ ...p, pan: (v || '').toUpperCase() }))} placeholder="AAACA1234E" maxLength={10} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">GSTIN</div><Inp value={f.gstin} onChange={v => setF(p => ({ ...p, gstin: (v || '').toUpperCase() }))} placeholder="07AAACA1234E1Z5" maxLength={15} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">State</div><Sl options={[{ v: '', l: 'Select State' }, ...INDIA_STATES.map(s => ({ v: s, l: s }))]} value={f.state} onChange={v => setF(p => ({ ...p, state: v }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Company</div><Sl options={[{ v: '', l: '— None —' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value={f.company_id} onChange={v => setF(p => ({ ...p, company_id: v }))} /></div>
        </div>

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Banking</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-4">
          <div><div className="text-[14px] text-t2 mb-[3px]">Bank Name</div><Inp value={f.bank_name} onChange={v => setF(p => ({ ...p, bank_name: v }))} placeholder="HDFC Bank" /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Account Number</div><Inp value={f.account_number} onChange={v => setF(p => ({ ...p, account_number: v }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">IFSC Code</div><Inp value={f.ifsc_code} onChange={v => setF(p => ({ ...p, ifsc_code: (v || '').toUpperCase() }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Payment Mode</div><Sl options={[{ v: 'NEFT', l: 'NEFT' }, { v: 'RTGS', l: 'RTGS' }, { v: 'IMPS', l: 'IMPS' }, { v: 'UPI', l: 'UPI' }, { v: 'CHEQUE', l: 'Cheque' }, { v: 'CASH', l: 'Cash' }]} value={f.payment_mode} onChange={v => setF(p => ({ ...p, payment_mode: v }))} /></div>
        </div>

        <div className="text-[14px] font-bold text-t3 uppercase tracking-wide mb-2">Contact &amp; Notes</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-3">
          <div><div className="text-[14px] text-t2 mb-[3px]">Contact Person <span className="text-r">*</span></div><Inp value={f.contact_person} onChange={v => setF(p => ({ ...p, contact_person: v }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Phone <span className="text-r">*</span></div><Inp value={f.contact_phone} onChange={v => setF(p => ({ ...p, contact_phone: v }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Email</div><Inp value={f.contact_email} onChange={v => setF(p => ({ ...p, contact_email: v }))} /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Address</div><Inp value={f.address} onChange={v => setF(p => ({ ...p, address: v }))} /></div>
          <div className="sm:col-span-2"><div className="text-[14px] text-t2 mb-[3px]">Notes</div><Inp value={f.notes} onChange={v => setF(p => ({ ...p, notes: v }))} /></div>
        </div>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-[6px]">
          <Bt full onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};
